import { useLayoutEffect, useRef, useState } from 'react'
import FlipInfoIcon from './FlipInfoIcon'
import { PLACEHOLDER } from './format'

// Plain weight/colour, same rule ValueBox.jsx documents for its own dash -- an absent value must
// not inherit the emphasis of a present one. `leading-[1.2]`: see STRIP_LINE below.
// A figure wider than the 154 card ($999.9M, 123456%) truncates its digits instead of running into
// the next card; only then does `title` carry the whole value. README.md ("Long values truncate").
function ValueParts({ prefix, body, suffix, colorCls }) {
  const bodyRef = useRef(null)
  const [clipped, setClipped] = useState(false)
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return undefined
    let live = true
    const measure = () => {
      if (live) setClipped(el.scrollWidth > el.clientWidth)
    }
    measure()
    // Figtree may land after the first layout and widen the digits.
    document.fonts?.ready?.then(measure)
    return () => {
      live = false
    }
  }, [prefix, body, suffix])

  if (body == null) {
    return <span className="text-[36px] leading-[1.2] font-normal text-brand-placeholder">{PLACEHOLDER}</span>
  }
  return (
    <span
      className="inline-flex max-w-full items-baseline leading-[1.2]"
      title={clipped ? `${prefix ?? ''}${body}${suffix ?? ''}` : undefined}
    >
      {prefix ? <span className={`shrink-0 text-[28px] font-extrabold ${colorCls}`}>{prefix}</span> : null}
      <span ref={bodyRef} className={`min-w-0 truncate text-[36px] font-extrabold ${colorCls}`}>{body}</span>
      {suffix ? <span className={`shrink-0 text-[28px] font-extrabold ${colorCls}`}>{suffix}</span> : null}
    </span>
  )
}

// Letter 99 (7.4.1): label and value sit 14px from the strip's top and bottom border, set as padding
// on a line-height of 1.2 (Figtree's own text box, so 14 + 16.8 + 43.2 + 14 = 88), not by centring.
// DealScoreBlock uses the same numbers, which keeps the two baselines shared (spec 0.4). README.md
// ("Deal Score & Metrics: 14px from the border").
export const STRIP_PADDING_CLS = 'pt-[14px] pb-[14px]'

// 154 wide and the strip's full 88 tall, no own fill/border/radius -- the white comes from
// MetricsStrip's own container. The divider is still 56 tall, 16 in from the container's top and
// bottom (spec 2.2), drawn as its own element now that the card is no longer the 56px box.
//
// `floating`: this card only ever renders inside MetricsStrip's overflow-x row, which would clip an
// absolute panel below 1280 (README.md, "Metric tooltips float above the strip's overflow wrapper").
export default function MetricCard({ label, tooltip, prefix, body, suffix, negative, withDivider, testId }) {
  const colorCls = negative ? 'text-brand-statusSale' : 'text-brand-dark'
  return (
    <div data-testid={testId} className={`relative w-[154px] h-[88px] shrink-0 flex flex-col ${STRIP_PADDING_CLS}`}>
      <span className="inline-flex items-center gap-x-[6px]">
        <span className={`text-[14px] leading-[1.2] font-normal ${colorCls}`}>{label}</span>
        <FlipInfoIcon tooltip={tooltip} floating />
      </span>
      <ValueParts prefix={prefix} body={body} suffix={suffix} colorCls={colorCls} />
      {withDivider ? (
        <span data-testid="metric-divider" aria-hidden="true" className="absolute right-0 top-[16px] h-[56px] w-px bg-brand-fieldBdr" />
      ) : null}
    </div>
  )
}
